/* ============================================================
   Review — the week, read calmly: two editorial numbers and the shape of the
   week as seven soft bars, the habit tracker with its rings, the completion
   matrix, and what was finished. Arrows walk weeks. No score, no grade —
   a trend line in words, never red.
   ============================================================ */
import { useMemo, useState } from 'react';
import { addDaysStr, overdueLabel, toDayStr, todayStr, useStore, useWeekStart, weekStartOf, weekStats, weekdayLabels } from '@habishine/client-core';

import { Heatmap } from '../components/Heatmap';
import { Icon } from '../components/Icon';
import { Ring } from '../components/Ring';
import { CardGroup, DoneRow, SectionHead, Toggle } from '../components/rows';
import { Page, PageHeader } from '../components/ui';
import { useEditors } from '../shell/Editors';
import { hueVar } from '../theme/hues';

export function ReviewPage() {
  const { state, orderedHabits, hueForTask } = useStore();
  const { openTask, openHabit } = useEditors();
  const { weekStartsOn } = useWeekStart();
  const [offset, setOffset] = useState(0);
  const today = todayStr();
  const thisWeek = toDayStr(weekStartOf(new Date(), weekStartsOn));
  const weekStart = addDaysStr(thisWeek, offset * 7);
  const weekEnd = addDaysStr(weekStart, 6);
  const stats = useMemo(() => weekStats(state, weekStart), [state, weekStart]);
  const prev = useMemo(() => weekStats(state, addDaysStr(weekStart, -7)), [state, weekStart]);
  const habits = orderedHabits();
  const pctOf = new Map(state.habits.map((h, i) => [h.id, stats.pcts[i] ?? 0]));
  const trend = stats.checkIns > prev.checkIns ? 'up' : stats.checkIns < prev.checkIns ? 'down' : 'flat';
  const [doneOpen, setDoneOpen] = useState(false);
  const fmt = (d: string) => new Date(d.replace(/-/g, '/')).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const quiet = stats.tasksDone === 0 && stats.checkIns === 0;

  // the week's shape: per day, check-ins kept and tasks finished
  const days = useMemo(() => {
    const labels = weekdayLabels(weekStartsOn);
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDaysStr(weekStart, i);
      const reps = state.habits.reduce((n, h) => n + (state.checkins[h.id]?.[d]?.completed ? 1 : 0), 0);
      const tasks = stats.completed.filter((t) => t.doneAt === d).length;
      return { d, label: labels[i] ?? '', reps, tasks, future: d > today };
    });
  }, [state, stats, weekStart, weekStartsOn, today]);
  const peak = Math.max(1, ...days.map((x) => x.reps + x.tasks));
  const bestDay = days.reduce((b, x) => (x.reps + x.tasks > (b?.reps ?? 0) + (b?.tasks ?? 0) ? x : b), days[0]);

  const trendLine =
    trend === 'up' ? 'More habits kept than the week before.'
    : trend === 'down' ? 'A little lighter than the week before — that’s okay.'
    : 'Holding steady with the week before.';

  return (
    <Page className="screen-in">
      <PageHeader
        hue="green"
        eyebrow={`${offset === 0 ? 'This week' : offset === -1 ? 'Last week' : `Week of ${fmt(weekStart)}`} · ${fmt(weekStart)} – ${fmt(weekEnd)}`}
        title="Review"
        right={
          <>
            {offset !== 0 ? <button type="button" onClick={() => setOffset(0)} className="rounded-full bg-card-sunk px-3 py-1.5 text-[13px] font-bold text-frog">This week</button> : null}
            <button type="button" aria-label="Previous week" onClick={() => setOffset((o) => o - 1)} className="rounded-full p-1.5 text-tertiary transition hover:bg-card-sunk hover:text-ink"><Icon name="chevron-left" size={18} strokeWidth={2.4} /></button>
            <button type="button" aria-label="Next week" disabled={offset >= 0} onClick={() => setOffset((o) => o + 1)} className="rounded-full p-1.5 text-tertiary transition hover:bg-card-sunk hover:text-ink disabled:opacity-30"><Icon name="chevron-right" size={18} strokeWidth={2.4} /></button>
          </>
        }
      />

      {/* the wins card: numbers in the display face, the week as bars beside them */}
      <div className="rise-in surface relative overflow-hidden p-5 md:p-6" style={{ ['--i' as string]: 0 }}>
        <span className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full opacity-70" style={{ background: 'radial-gradient(closest-side, var(--hue-green-t), transparent)' }} aria-hidden />
        <div className="relative grid gap-8 md:grid-cols-[auto_1fr]">
          <div className="flex gap-8 md:gap-10">
            <Stat n={stats.tasksDone} label={stats.tasksDone === 1 ? 'task done' : 'tasks done'} hue="green" />
            <Stat n={stats.checkIns} label={stats.checkIns === 1 ? 'habit check-in' : 'habit check-ins'} hue="purple" />
          </div>
          <div className="flex min-w-0 flex-col justify-end">
            <div className="flex items-end gap-2">
              {days.map((x) => {
                const total = x.reps + x.tasks;
                return (
                  <div key={x.d} className="flex flex-1 flex-col items-center gap-1.5" title={`${x.reps} check-ins · ${x.tasks} tasks`}>
                    <div className="relative h-[64px] w-full max-w-[36px] overflow-hidden rounded-[8px] bg-card-sunk">
                      <div className="absolute inset-x-0 bottom-0 flex flex-col justify-end overflow-hidden rounded-[8px]" style={{ height: `${(total / peak) * 100}%`, transition: 'height .5s var(--ease-out)' }}>
                        <div style={{ flex: x.tasks, background: hueVar('green', 'c') }} />
                        <div style={{ flex: x.reps, background: hueVar('purple', 'c') }} />
                      </div>
                    </div>
                    <span className={`text-[10.5px] font-bold uppercase ${x.d === today ? 'text-frog' : 'text-tertiary'}`}>{x.label.slice(0, 1)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <p className="relative mt-5 text-[14px] leading-[1.5] text-ink-2">
          {quiet ? 'A quiet week — a fresh one starts whenever you do. ' : bestDay && bestDay.reps + bestDay.tasks > 0 ? `${bestDay.label} was the fullest day. ` : ''}
          <span className="text-meta">{trendLine}</span>
        </p>
      </div>

      <SectionHead count={habits.length}>Habit tracker</SectionHead>
      <CardGroup index={1}>
        {habits.length === 0 ? <p className="px-4 py-3 text-sm text-meta">No habits yet.</p> : null}
        {habits.map((h, i) => {
          const pct = pctOf.get(h.id) ?? 0;
          return (
            <button key={h.id} type="button" onClick={() => openHabit(h.id, todayStr())} className={`row-hover flex w-full items-center gap-3.5 px-4 py-3 text-left ${i > 0 ? 'border-t border-hairline' : ''}`}>
              <span className="relative h-[46px] w-[46px] shrink-0">
                <Ring size={46} stroke={5} hue={h.hue} pct={pct} />
                <span className="absolute inset-0 flex items-center justify-center text-base">{h.emoji || '✦'}</span>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15.5px] font-semibold text-ink">{h.name}</span>
                <span className="block text-[13px] text-meta">{h.streak > 0 ? `🔥 ${h.streak}-${h.unit} run` : 'Fresh start'} · best {h.best ?? 0}</span>
              </span>
              <span className="font-display num text-[22px] font-semibold" style={{ color: hueVar(h.hue, 'k') }}>{Math.round(pct * 100)}<span className="text-[13px] font-sans font-bold">%</span></span>
              <span className="text-tertiary"><Icon name="chevron-right" size={16} /></span>
            </button>
          );
        })}
      </CardGroup>

      {habits.length > 0 ? <div className="rise-in mt-4" style={{ ['--i' as string]: 2 }}><Heatmap habits={habits} checkins={state.checkins} /></div> : null}

      {stats.completed.length > 0 ? (
        <>
          <Toggle label={`Finished this week (${stats.completed.length})`} open={doneOpen} onToggle={() => setDoneOpen((o) => !o)} />
          {doneOpen ? (
            <CardGroup>
              {stats.completed.map((t, i) => (
                <DoneRow key={t.id} task={t} hue={hueForTask(t)} first={i === 0} note={t.doneAt ? overdueLabel(t.doneAt) : undefined} onOpen={() => openTask(t.id, t.doneAt ?? todayStr())} />
              ))}
            </CardGroup>
          ) : null}
        </>
      ) : null}
    </Page>
  );
}

function Stat({ n, label, hue }: { n: number; label: string; hue: 'green' | 'purple' }) {
  return (
    <div>
      <div className="font-display num text-[52px] font-semibold leading-[0.9] md:text-[64px]" style={{ color: hueVar(hue, 'k') }}>{n}</div>
      <div className="mt-2 text-[13px] font-semibold text-meta">{label}</div>
    </div>
  );
}
